import React from 'react';
import styled from 'styled-components';

interface QuantityStepperInputProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  disabled: boolean;
}

const QuantityStepperInput: React.FC<QuantityStepperInputProps> = ({
  value,
  onChange,
  min,
  max,
  disabled,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // for single digit make backspace and delete select all instead of deleting the text
    if ((e.key === 'Backspace' || e.key === 'Delete') && Number(e.currentTarget.value) < 10) {
      e.preventDefault();
      e.currentTarget.select();
      return;
    }

    // allow digits, navigation, editing, clipboard shortcuts
    const allowed =
      (e.key >= '0' && e.key <= '9') ||
      [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
        'Tab',
        'Enter',
      ].includes(e.key) ||
      ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase()));

    if (!allowed) {
      e.preventDefault();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue === '') {
      onChange(min);
      return;
    }

    const newValue = parseInt(inputValue, 10);
    if (!isNaN(newValue)) {
      const clampedValue = Math.min(Math.max(newValue, min), max);
      onChange(clampedValue);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const displayValue = isNaN(value) ? min : value;

  return (
    <StyledQuantityInput
      data-testid="quantity-input"
      disabled={disabled}
      inputMode="numeric"
      max={max}
      min={min}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      pattern="[0-9]*"
      value={displayValue}
    />
  );
};

const StyledQuantityInput = styled.input.attrs({ type: 'number' })`
  border: none;
  background: transparent;
  text-align: center;
  width: 40px;
  font-weight: 500;
  font-family: ${(props) => props.theme.regularTextFont};
  color: ${(props) => props.theme.colors.accent};
  outline: none;

  /* hide browser number input spinners */
  appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }

  &:disabled {
    color: ${(props) => props.theme.colors.disabled};
  }
`;

export default React.memo(QuantityStepperInput);
